// FootballField — a valuation range chart ("football field"), ported from the
// valuation mockup hero. Each method draws a low→high horizontal range with a
// midpoint marker; a dashed vertical line marks the blended midpoint. Income
// methods (DCF / multiples) read teal; market-comparable methods read anchor.
// Inline SVG, no chart libraries, colorblind-safe (position + color + labels).

export type FootballMethod = {
  id: string
  label: string
  low: number
  mid: number
  high: number
  /** Market/comparable methods render in the neutral anchor tone. */
  market?: boolean
  /**
   * When `false`, the method has no valid value under the current inputs/scenario
   * (e.g. an EBITDA multiple while EBITDA is negative). The row is still drawn —
   * muted, with `unusedReason` in place of a range bar — so the method stays
   * visible and can light up when a slider makes it valid again. Defaults to
   * applicable when omitted.
   */
  applicable?: boolean
  /** Short reason shown on an unused row, e.g. "Not used — EBITDA is not positive". */
  unusedReason?: string
}

export type FootballFieldProps = {
  methods: FootballMethod[]
  /** Blended midpoint marker (vertical dashed line). */
  blended?: number | null
  ariaLabel: string
  formatValue?: (value: number) => string
  width?: number
  rowHeight?: number
  className?: string
}

export type FootballScale = { min: number; max: number }

/** Compute the shared x-scale across all method ranges + blend. Exported for tests. */
export function computeFootballScale(methods: FootballMethod[], blended?: number | null): FootballScale {
  const vals: number[] = []
  for (const m of methods) {
    if (m.applicable === false) continue
    for (const v of [m.low, m.mid, m.high]) if (Number.isFinite(v)) vals.push(v)
  }
  if (blended != null && Number.isFinite(blended)) vals.push(blended)
  if (!vals.length) return { min: 0, max: 1 }
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const pad = (max - min || Math.abs(max) || 1) * 0.12
  return { min: Math.max(0, min - pad), max: max + pad }
}

export function FootballField({
  methods,
  blended = null,
  ariaLabel,
  formatValue,
  width = 920,
  rowHeight = 50,
  className,
}: FootballFieldProps) {
  const labelW = 200
  const padR = 40
  const padTop = 28
  const trackX = labelW
  const trackW = width - labelW - padR
  const scale = computeFootballScale(methods, blended)
  const span = scale.max - scale.min || 1
  const toX = (v: number) => trackX + ((v - scale.min) / span) * trackW
  const height = padTop + methods.length * rowHeight + 16
  const barH = 16

  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      {blended != null && Number.isFinite(blended) ? (
        <>
          <line x1={toX(blended)} y1={padTop - 14} x2={toX(blended)} y2={height - 14} stroke="var(--pos, #0e7c86)" strokeWidth={2} strokeDasharray="6 5" />
          {formatValue ? (
            <text x={toX(blended)} y={padTop - 18} textAnchor="middle" style={{ font: '700 11px var(--font-ui)', fill: 'var(--pos)' }}>
              Blended {formatValue(blended)}
            </text>
          ) : null}
        </>
      ) : null}
      {methods.map((m, i) => {
        const cy = padTop + i * rowHeight + rowHeight / 2
        if (m.applicable === false) {
          // Keep the method visible but muted: label plus the reason it is not
          // contributing, so moving a slider (e.g. EBITDA margin) can bring it back.
          return (
            <g key={`${m.id}-${i}`}>
              <text x={labelW - 14} y={cy + 4} textAnchor="end" style={{ font: '600 12px var(--font-ui)', fill: 'var(--muted, #9aa0a8)' }}>
                {m.label}
              </text>
              <line x1={trackX} y1={cy} x2={trackX + trackW} y2={cy} stroke="var(--hair, #d7dbe0)" strokeWidth={1} strokeDasharray="4 5" opacity={0.7} />
              <text x={trackX + 8} y={cy - 6} textAnchor="start" style={{ font: 'italic 500 11px var(--font-ui)', fill: 'var(--muted, #9aa0a8)' }}>
                {m.unusedReason || 'Not used in the current scenario'}
              </text>
            </g>
          )
        }
        const x1 = toX(m.low)
        const x2 = toX(m.high)
        const xm = toX(m.mid)
        const color = m.market ? 'var(--anchor, #9aa0a8)' : 'var(--pos, #0e7c86)'
        return (
          <g key={`${m.id}-${i}`}>
            <text x={labelW - 14} y={cy + 4} textAnchor="end" style={{ font: '600 12px var(--font-ui)', fill: 'var(--ink-2, #4c5360)' }}>
              {m.label}
            </text>
            <rect x={Math.min(x1, x2)} y={cy - barH / 2} width={Math.abs(x2 - x1)} height={barH} rx={barH / 2} fill={color} opacity={m.market ? 0.2 : 0.16} />
            <rect x={Math.min(x1, x2)} y={cy - barH / 2} width={Math.abs(x2 - x1)} height={barH} rx={barH / 2} fill="none" stroke={color} strokeWidth={1.2} opacity={m.market ? 1 : 0.5} />
            <circle cx={xm} cy={cy} r={5} fill="var(--pos, #0e7c86)" />
            {formatValue ? (
              <>
                <text x={x1 - 8} y={cy + 4} textAnchor="end" style={{ font: '600 10px var(--font-ui)', fill: 'var(--muted)' }}>
                  {formatValue(m.low)}
                </text>
                <text x={x2 + 8} y={cy + 4} textAnchor="start" style={{ font: '600 10px var(--font-ui)', fill: 'var(--muted)' }}>
                  {formatValue(m.high)}
                </text>
              </>
            ) : null}
          </g>
        )
      })}
    </svg>
  )
}

export default FootballField
